import { useCallback, useMemo } from "react";
import { useCurrentUser } from "../providers/CurrentUserProvider";
import type { Clause, Policy, Scope } from "./policy";


function toArr(v?: string | string[]) { return v == null ? [] : Array.isArray(v) ? v : [v]; }

export function useAccess() {
  const { roles, acRoles, airflowRoles } = useCurrentUser();

  const isDemo = useMemo(() => {
    const v = process.env.REACT_APP_IS_DEMO;
    return String(v).toLowerCase() === "true";
  }, []);

  const isReadAcross = useMemo(() => {
    const v = process.env.REACT_APP_IS_READACROSS;
    return String(v).toLowerCase() === "true";
  }, []);

  const appType = useMemo(() => {
    const v = process.env.REACT_APP_APPLICATION ?? "";
    return String(v).toLowerCase();
  }, []);

  const pickSet = useCallback((s: Scope) => (s === "ac" ? acRoles : s === "airflow" ? airflowRoles : roles),
    [roles, acRoles, airflowRoles]
  );

  const matchRoles = useCallback((set: Set<string>, c?: Clause) => {
    if (!c) return true;
    const { rolesAnyOf, rolesAllOf, rolesNot } = c;
    if (rolesNot?.some(r => set.has(r))) return false;
    if (rolesAllOf && !rolesAllOf.every(r => set.has(r))) return false;
    if (rolesAnyOf && !rolesAnyOf.some(r => set.has(r))) return false;
    return true;
  }, []);

  const matchDemo = useCallback((c?: Clause) => {
    const filter = c?.demo ?? "any";
    if (filter === "only") return isDemo;
    if (filter === "exclude") return !isDemo;
    return true;
  }, [isDemo]);

  const matchReadAcross = useCallback((c?: Clause) => {
    const filter = c?.readAcross ?? "any";
    if (filter === "only") return isReadAcross;
    if (filter === "exclude") return !isReadAcross;
    return true;
  }, [isReadAcross]);

  const matchApp = useCallback((c?: Clause) => {
    if (!c) return true;
    const allow = toArr(c.appIs).map(s => s.toLowerCase());
    const deny  = toArr(c.appNot).map(s => s.toLowerCase());
    if (deny.length && deny.includes(appType)) return false;
    if (allow.length && !allow.includes(appType)) return false;
    return true;
  }, [appType]);

  const matchClause = useCallback((c: Clause, defaultScope: Scope = "any") => {
    const set = pickSet(c.roleScope ?? defaultScope);
    return matchDemo(c) && matchReadAcross(c) && matchApp(c) && matchRoles(set, c) && (c.predicate ? c.predicate() : true);
  }, [pickSet, matchDemo, matchApp, matchRoles, matchReadAcross]);

  // The programmatic check you can call anywhere inside components
  const canAccess = useCallback((policy: Policy, defaultScope: Scope = "any") => {
    if ("or" in policy) return policy.or.some(c => matchClause(c, defaultScope));
    return matchClause(policy as Clause, defaultScope);
  }, [matchClause]);

  return { canAccess, isDemo, appType, roles, acRoles, airflowRoles };
}
